import { Image } from "antd";
import BChatItem from "components/base/BChatItem/BChatItem";
import { useCallback, useState } from "react";
import type { ChatFile } from "types/chat.type";
import type { Message } from "types/message";
import { MessageRole } from "types/message";
import "./MessageItem.scss";

type MessageItemProps = {
  message: Message;
  status?: "done" | "loading" | "streaming";
};

type PreviewState = {
  open: boolean;
  image: string;
};

const formatMessageTime = (createdAt: number) => {
  const messageDate = new Date(createdAt * 1000);
  const today = new Date();
  const isToday =
    messageDate.getDate() === today.getDate() &&
    messageDate.getMonth() === today.getMonth() &&
    messageDate.getFullYear() === today.getFullYear();

  if (isToday) {
    return messageDate.toLocaleString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return messageDate.toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MessageItem = ({ message, status = "done" }: MessageItemProps) => {
  const isUser = message.role === MessageRole.User;
  const role = isUser ? "user" : "assistant";
  const imageFiles = (message.files ?? []).filter((file) => file.kind === "image");
  const [preview, setPreview] = useState<PreviewState>({ open: false, image: "" });

  const handlePreview = useCallback((file: ChatFile) => {
    setPreview({ open: true, image: file.url });
  }, []);

  return (
    <div className="message-item">
      {imageFiles.length > 0 ? (
        <div className="message-item__files" data-user={isUser || undefined}>
          {imageFiles.map((file) => (
            <button
              key={file.id}
              type="button"
              className="message-item__file"
              onClick={() => handlePreview(file)}
              aria-label={`Xem ${file.name}`}
            >
              <img className="message-item__image" src={file.url} alt={file.name} />
            </button>
          ))}
        </div>
      ) : null}

      {message.content || status !== "done" ? (
        <BChatItem role={role} content={message.content} status={status} />
      ) : null}

      <div className="message-item__meta" data-user={isUser || undefined}>
        {isUser ? "Bạn" : "BMD AI"} · {formatMessageTime(message.createdAt)}
      </div>

      {preview.image ? (
        <Image
          style={{ display: "none" }}
          preview={{
            visible: preview.open,
            onVisibleChange: (visible) => setPreview((prev) => ({ ...prev, open: visible })),
            afterOpenChange: (visible) => {
              if (!visible) {
                setPreview({ open: false, image: "" });
              }
            },
          }}
          src={preview.image}
        />
      ) : null}
    </div>
  );
};

export default MessageItem;
